import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Alert, AlertDescription } from './ui/alert';
import { Loader2, AlertCircle, CheckCircle, User, Mail, Camera, Sparkles } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { useLocale } from '../context/LocaleContext';

const EditProfileModal = ({ isOpen, onClose, user, onUpdate }) => {
  const { toast } = useToast();
  const { t } = useLocale();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    picture: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (user) {
      setFormData({
        name: user.name || '',
        email: user.email || '',
        picture: user.picture || ''
      });
    }
  }, [user]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    setError('');
    setSuccess(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    e.stopPropagation(); // Prevent any event bubbling
    setLoading(true);
    setError('');
    setSuccess(false);

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/user/profile`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify(formData)
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Failed to update profile');
      }

      const data = await response.json();
      setSuccess(true);
      
      // Show success toast
      toast({
        title: '✅ Profile Updated',
        description: 'Your profile has been saved successfully',
      });
      
      // Call the onUpdate callback to refresh user data in parent
      if (onUpdate) {
        try {
          await onUpdate(data.user);
        } catch (updateError) {
          console.error('Error refreshing user:', updateError);
          // Continue anyway - modal will still close
        }
      }

      // Close modal after a short delay to show success message
      setTimeout(() => {
        if (onClose && typeof onClose === 'function') {
          onClose();
        }
        setSuccess(false);
      }, 1000);

    } catch (err) {
      console.error('Profile update error:', err);
      setError(err.message || 'An error occurred while updating profile');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[550px] p-0 overflow-hidden">
        {/* Gradient Header */}
        <div className="bg-gradient-to-r from-teal-600 via-cyan-600 to-orange-500 h-24"></div>
        
        <div className="px-8 pb-8">
          {/* User Icon - Overlapping Header */}
          <div className="flex justify-center -mt-16 mb-6">
            <div className="h-28 w-28 rounded-2xl border-4 border-white shadow-2xl bg-gradient-to-br from-teal-600 via-cyan-600 to-blue-600 flex items-center justify-center">
              <User className="h-14 w-14 text-white" />
            </div>
          </div>

          {/* Header Text */}
          <div className="text-center mb-6">
            <h2 className="text-3xl font-bold bg-gradient-to-r from-teal-600 to-cyan-600 bg-clip-text text-transparent mb-2">
              {t('settings.editProfile')}
            </h2>
            <p className="text-gray-600 flex items-center justify-center">
              <Sparkles className="w-4 h-4 mr-2 text-teal-500" />
              {t('settings.updateProfile') || 'Update your personal information'}
            </p>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="space-y-5">
              {/* Name Field */}
              <div className="space-y-2">
                <Label htmlFor="name" className="text-gray-700 font-semibold flex items-center">
                  <User className="w-4 h-4 mr-2 text-teal-500" />
                  {t('auth.name') || 'Full Name'}
                </Label>
                <Input
                  id="name"
                  name="name"
                  type="text"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Enter your full name"
                  required
                  className="h-12 border-2 border-gray-200 focus:border-teal-500 focus:ring-teal-500 rounded-xl transition-all duration-300"
                />
              </div>

              {/* Email Field (Read-only) */}
              <div className="space-y-2">
                <Label htmlFor="email" className="text-gray-700 font-semibold flex items-center">
                  <Mail className="w-4 h-4 mr-2 text-teal-500" />
                  Email Address
                </Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="Enter your email"
                  disabled
                  className="h-12 bg-gradient-to-r from-gray-50 to-gray-100 border-2 border-gray-200 rounded-xl cursor-not-allowed"
                />
                <div className="bg-blue-50 border-l-4 border-blue-400 p-3 rounded-r-lg">
                  <p className="text-xs text-blue-700 font-medium">
                    🔒 Email cannot be changed as it's linked to your account
                  </p>
                </div>
              </div>

              {/* Error Alert */}
              {error && (
                <Alert variant="destructive" className="border-2 border-red-300 bg-red-50 animate-in slide-in-from-top duration-300">
                  <AlertCircle className="h-5 w-5" />
                  <AlertDescription className="font-medium">{error}</AlertDescription>
                </Alert>
              )}

              {/* Success Alert */}
              {success && (
                <Alert className="border-2 border-green-300 bg-green-50 text-green-800 animate-in slide-in-from-top duration-300">
                  <CheckCircle className="h-5 w-5" />
                  <AlertDescription className="font-medium">✨ Profile updated successfully!</AlertDescription>
                </Alert>
              )}
            </div>

            {/* Action Buttons */}
            <div className="flex space-x-3 mt-8">
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
                disabled={loading}
                className="flex-1 h-12 rounded-xl border-2 border-gray-300 hover:bg-gray-100 font-semibold transition-all duration-200"
              >
                Cancel
              </Button>
              <Button 
                type="submit" 
                disabled={loading}
                className="flex-1 h-12 bg-gradient-to-r from-teal-600 via-cyan-600 to-orange-500 hover:from-teal-700 hover:via-cyan-700 hover:to-orange-600 text-white rounded-xl shadow-lg hover:shadow-xl transform hover:scale-[1.02] transition-all duration-200 font-bold"
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Sparkles className="mr-2 h-5 w-5" />
                    Save Changes
                  </>
                )}
              </Button>
            </div>
          </form>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default EditProfileModal;
